import React, { useState, useEffect } from 'react';
import { Typography } from '@chr/eds-react';
import {
  Button,
  Grid,
  Stack,
  useMediaQuery,
  useTheme,
  TextField,
} from '@mui/material';
import { useTranslation } from '@chr/online-shared-ui-localizations';
import { useAdobeActions } from '../../common/hooks/useAnalyticsUIEvents';
import { useChrLdFlag } from '@chr/common-launchdarkly';
import {
  REFERENCE_NUMBER_MAX_LENGTH,
  POSTAL_CODE_MAX_LENGTH,
  isValidInput,
  isValidPostalCodeInput,
  getInputErrorMessage,
  getPostalCodeErrorMessage
} from '../../common/utilities/inputValidation';

// --- Shared style constants ---

const getTextFieldSx = (isMobileScreen = false) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white',
    borderRadius: '4px',
    height: '46px',
    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: 'red',
    },
  },
  '& input': { height: 'auto' },
  '& .MuiFormHelperText-root': {
    ...(isMobileScreen ? { position: 'relative' } : { position: 'absolute', top: '100%' }),
    '&.Mui-error': {
      color: 'white !important',
      '& *': {
        color: 'white !important',
        fill: 'white !important',
      },
      '&::before, &::after': {
        color: 'white !important',
      },
      '& *::before, & *::after': {
        color: 'white !important',
      },
      '& svg, & svg path': {
        color: 'white !important',
        fill: 'white !important',
      },
    },
  },
});

// Keep a default for non-reference fields
const textFieldSx = getTextFieldSx(false);

const getToggleButtonSx = (
  isActive: boolean,
  side: 'left' | 'right',
  isMobileScreen: boolean
) => {
  const borderRadius = side === 'left' ? '4px 0 0 4px' : '0 4px 4px 0';

  const baseSx = {
    borderRadius,
    color: 'white',
    minWidth: '140px',
    height: '46px',
    ...(isMobileScreen ? { flex: 1 } : {}),
    textTransform: 'none' as const,
    fontSize: '14px',
    fontWeight: 600,
    border: '1px solid white',
    ...(side === 'right' ? { borderLeft: '1px solid white' } : {}),
  };

  if (isActive) {
    return {
      ...baseSx,
      backgroundColor: 'primary.main',
      '&:hover, &:active': {
        border: '1px solid white',
        color: 'white',
        backgroundColor: 'primary.main',
      },
    };
  }

  return {
    ...baseSx,
    border: '1px solid rgba(255,255,255,0.5)',
    backgroundColor: 'transparent',
    '&:hover': {
      border: '1px solid rgba(255,255,255,0.5)',
      color: 'white',
      backgroundColor: 'rgba(121, 190, 239, 0.2)',
    },
    '&:active': {
      border: '1px solid rgba(255,255,255,0.5)',
      color: 'white',
      backgroundColor: 'rgba(121, 190, 239, 0.38)',
    }
  };
};

const trackButtonSx = {
  backgroundColor: 'primary.main',
  color: 'white',
  border: '1px solid white',
  height: '46px',
  minWidth: '100px',
  textTransform: 'none' as const,
  fontSize: '16px',
  fontWeight: 600,
  borderRadius: '4px',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: 'primary.dark',
    color: 'white',
    boxShadow: 'none',
  },
  '&:disabled': {
    color: 'white',
    opacity: 0.5,
  },
};

const legacyTrackButtonSx = {
  background: 'primary',
  color: 'white',
  borderColor: 'white',
  height: '56px',
  textTransform: 'none' as const,
  fontSize: '16px',
  '&:hover': {
    color: '#000000',
    borderColor: '#000000',
    backgroundColor: '#FFFFFF',
  },
  '&:disabled': {
    color: '#EEEFEF',
    borderColor: '#EEEFEF',
  },
};

// --- Sub-components ---

interface TitleSectionProps {
  isMobileScreen: boolean;
  translate: (key: string) => string;
}

const TitleSection: React.FC<TitleSectionProps> = ({ isMobileScreen, translate }) => (
  <Grid item sm={12}>
    <Stack
      direction={isMobileScreen ? 'column-reverse' : 'row'}
      spacing={2}
      sx={{ justifyContent: 'space-between' }}
    >
      {isMobileScreen ? (
        <Typography variant="h2">{translate('C.H. Robinson Tracking')}</Typography>
      ) : (
        <Typography variant="h3">{translate('C.H. Robinson Tracking')}</Typography>
      )}
    </Stack>
  </Grid>
);

interface SearchToggleButtonsProps {
  activeSearch: 'tracking' | 'reference';
  setActiveSearch: (value: 'tracking' | 'reference') => void;
  isMobileScreen: boolean;
  translate: (key: string) => string;
}

const SearchToggleButtons: React.FC<SearchToggleButtonsProps> = ({
  activeSearch, setActiveSearch, isMobileScreen, translate,
}) => (
  <Stack direction="column" spacing={0.5}>
    <Typography variant="subtitle300" fontWeight="regular">
      {translate('Search Input')}
    </Typography>
    <Stack direction="row" spacing={0} sx={{ width: '100%' }}>
      <Button
        variant="outlined"
        onClick={() => setActiveSearch('tracking')}
        sx={getToggleButtonSx(activeSearch === 'tracking', 'left', isMobileScreen)}
      >
        {translate('Tracking No.')}
      </Button>
      <Button
        variant="outlined"
        onClick={() => setActiveSearch('reference')}
        sx={getToggleButtonSx(activeSearch === 'reference', 'right', isMobileScreen)}
      >
        {translate('Reference No.')}
      </Button>
    </Stack>
  </Stack>
);

interface TrackingInputProps {
  chrTrackingNumber: string;
  setChrTrackingNumber: (value: string) => void;
  onTrack: (trackingNumber: string) => void;
  isMobileScreen: boolean;
  translate: (key: string) => string;
}

const TrackingInput: React.FC<TrackingInputProps> = ({
  chrTrackingNumber, setChrTrackingNumber, onTrack, isMobileScreen, translate,
}) => (
  <Stack direction="column" spacing={0.5} sx={{ flex: 1, ...(isMobileScreen ? {} : { maxWidth: '422px' }) }}>
    <Typography variant="subtitle300" fontWeight="regular">
      {translate('Tracking Number *')}
    </Typography>
    <TextField
      data-testid="chr-tracking-number"
      value={chrTrackingNumber}
      onChange={(e) => setChrTrackingNumber(e.target.value)}
      onKeyUp={(e) => {
        if (chrTrackingNumber && e.key === 'Enter') {
          onTrack(chrTrackingNumber);
        }
      }}
      fullWidth
      size="small"
      InputProps={{ notched: false }}
      sx={textFieldSx}
    />
  </Stack>
);

interface ReferenceInputsProps {
  referenceNumber: string;
  setReferenceNumber: (value: string) => void;
  postalCode: string;
  setPostalCode: (value: string) => void;
  onTrackReference: () => void;
  isMobileScreen: boolean;
  translate: (key: string) => string;
}

const ReferenceInputs: React.FC<ReferenceInputsProps> = ({
  referenceNumber, setReferenceNumber, postalCode, setPostalCode,
  onTrackReference, isMobileScreen, translate,
}) => {
  const referenceError = getInputErrorMessage(referenceNumber, translate);
  const postalCodeError = getPostalCodeErrorMessage(postalCode, translate);

  return (
  <>
    <Stack direction="column" spacing={0.5} sx={{ flex: 1, ...(isMobileScreen ? {} : { maxWidth: '256px' }) }}>
      <Typography variant="subtitle300">
        {translate('Reference Number *')}
      </Typography>
      <TextField
        data-testid="reference-number"
        value={referenceNumber}
        onChange={(e) => setReferenceNumber(e.target.value)}
        error={!!referenceError}
        helperText={referenceError}
        inputProps={{ maxLength: REFERENCE_NUMBER_MAX_LENGTH }}
        FormHelperTextProps={{
          sx: {
            '&.Mui-error': {
              color: 'white !important',
              '& svg, & svg path': { color: 'white !important', fill: 'white !important' },
              '& .MuiSvgIcon-root': { color: 'white !important' },
              '&::before, &::after, & *::before, & *::after': { color: 'white !important' },
            },
          },
        }}
        fullWidth
        size="small"
        InputProps={{ notched: false }}
        sx={getTextFieldSx(isMobileScreen)}
      />
    </Stack>
    <Stack direction="column" spacing={0.5} sx={{ flex: 1, ...(isMobileScreen ? {} : { maxWidth: '256px' }) }}>
      <Typography variant="subtitle300" fontWeight="regular">
        {translate('Origin or Destination Postal Code *')}
      </Typography>
      <TextField
        data-testid="postal-code"
        value={postalCode}
        onChange={(e) => setPostalCode(e.target.value)}
        onKeyUp={(e) => {
          if (referenceNumber && postalCode && e.key === 'Enter') {
            onTrackReference();
          }
        }}
        error={!!postalCodeError}
        helperText={postalCodeError}
        inputProps={{ maxLength: POSTAL_CODE_MAX_LENGTH }}
        FormHelperTextProps={{
          sx: {
            '&.Mui-error': {
              color: 'white !important',
              '& svg, & svg path': { color: 'white !important', fill: 'white !important' },
              '& .MuiSvgIcon-root': { color: 'white !important' },
              '&::before, &::after, & *::before, & *::after': { color: 'white !important' },
            },
          },
        }}
        fullWidth
        size="small"
        InputProps={{ notched: false }}
        sx={getTextFieldSx(isMobileScreen)}
      />
    </Stack>
  </>
  );
};

interface EnhancedSearchSectionProps {
  activeSearch: 'tracking' | 'reference';
  setActiveSearch: (value: 'tracking' | 'reference') => void;
  chrTrackingNumber: string;
  setChrTrackingNumber: (value: string) => void;
  referenceNumber: string;
  setReferenceNumber: (value: string) => void;
  postalCode: string;
  setPostalCode: (value: string) => void;
  handleTrack: (trackingNumber: string) => void;
  handleTrackReference: () => void;
  isMobileScreen: boolean;
  translate: (key: string) => string;
}

const EnhancedSearchSection: React.FC<EnhancedSearchSectionProps> = ({
  activeSearch, setActiveSearch, chrTrackingNumber, setChrTrackingNumber,
  referenceNumber, setReferenceNumber, postalCode, setPostalCode,
  handleTrack, handleTrackReference, isMobileScreen, translate,
}) => {
  const isTrackingActive = activeSearch === 'tracking';
  const buttonId = isTrackingActive ? 'track-btn' : 'track-reference-btn';
  const isDisabled = isTrackingActive
    ? !chrTrackingNumber
    : !isValidInput(referenceNumber) || !isValidPostalCodeInput(postalCode);
  const onClickTrack = () =>
    isTrackingActive ? handleTrack(chrTrackingNumber) : handleTrackReference();

  return (
    <Stack
      direction={isMobileScreen ? 'column' : 'row'}
      spacing={2}
      sx={{ alignItems: isMobileScreen ? 'stretch' : 'flex-end' }}
    >
      <SearchToggleButtons
        activeSearch={activeSearch}
        setActiveSearch={setActiveSearch}
        isMobileScreen={isMobileScreen}
        translate={translate}
      />
      {isTrackingActive && (
        <TrackingInput
          chrTrackingNumber={chrTrackingNumber}
          setChrTrackingNumber={setChrTrackingNumber}
          onTrack={handleTrack}
          isMobileScreen={isMobileScreen}
          translate={translate}
        />
      )}
      {!isTrackingActive && (
        <ReferenceInputs
          referenceNumber={referenceNumber}
          setReferenceNumber={setReferenceNumber}
          postalCode={postalCode}
          setPostalCode={setPostalCode}
          onTrackReference={handleTrackReference}
          isMobileScreen={isMobileScreen}
          translate={translate}
        />
      )}
      <Button
        id={buttonId}
        variant="contained"
        disabled={isDisabled}
        onClick={onClickTrack}
        fullWidth={isMobileScreen}
        sx={trackButtonSx}
      >
        {translate('Track')}
      </Button>
    </Stack>
  );
};

interface LegacySearchSectionProps {
  chrTrackingNumber: string;
  setChrTrackingNumber: (value: string) => void;
  handleTrack: (trackingNumber: string) => void;
  isMobileScreen: boolean;
  translate: (key: string) => string;
}

const LegacySearchSection: React.FC<LegacySearchSectionProps> = ({
  chrTrackingNumber, setChrTrackingNumber, handleTrack, isMobileScreen, translate,
}) => (
  <>
    <Typography variant="subtitle2">
      {translate('Tracking Number *')}
    </Typography>
    <Stack
      direction={isMobileScreen ? 'column' : 'row'}
      spacing={2}
      sx={{
        marginTop: '5px',
        alignItems: isMobileScreen ? 'center' : 'flexStart',
      }}
    >
      <TextField
        label=""
        data-testid="chr-tracking-number"
        value={chrTrackingNumber}
        onChange={(e) => setChrTrackingNumber(e.target.value)}
        onKeyUp={(e) => {
          if (chrTrackingNumber && e.key === 'Enter') {
            handleTrack(chrTrackingNumber);
          }
        }}
        fullWidth={isMobileScreen}
      />
      <Button
        id="track-btn"
        variant="outlined"
        disabled={!chrTrackingNumber}
        onClick={() => handleTrack(chrTrackingNumber)}
        fullWidth={isMobileScreen}
        sx={legacyTrackButtonSx}
      >
        {translate('Track')}
      </Button>
    </Stack>
  </>
);

export interface ShipmentDetailsHeaderProps {
  onTrack: (trackingNumber: string) => void;
  onTrackReference?: (referenceNumber: string, postalCode: string) => void;
  trackingNumber?: string;
  referenceNumber?: string;
  postalCode?: string;
}

export const ShipmentDetailsHeader = ({
  onTrack,
  onTrackReference,
  trackingNumber,
  referenceNumber: referenceNumberProp,
  postalCode: postalCodeProp,
}: ShipmentDetailsHeaderProps) => {
  const { breakpoints } = useTheme();
  const isMobileScreen = useMediaQuery(breakpoints.down('md'));
  const [chrTrackingNumber, setChrTrackingNumber] = useState<string>(
    trackingNumber ? decodeURIComponent(trackingNumber) : ''
  );
  const [referenceNumber, setReferenceNumber] = useState<string>(
    referenceNumberProp ? decodeURIComponent(referenceNumberProp) : ''
  );
  const [postalCode, setPostalCode] = useState<string>(
    postalCodeProp ?? ''
  );
  const [activeSearch, setActiveSearch] = useState<'tracking' | 'reference'>(
    trackingNumber && !referenceNumberProp && !postalCodeProp ? 'tracking' : 'reference'
  );
  const translate = useTranslation();
  const { handleAdobeAction } = useAdobeActions();
  const enhancedSearch = useChrLdFlag('enhanced-externaltracking-search', false);
  
  useEffect(() => {
    if (trackingNumber !== undefined) {
      setChrTrackingNumber(decodeURIComponent(trackingNumber));
    }
  }, [trackingNumber]);

  useEffect(() => {
    if (referenceNumberProp !== undefined) {
      setReferenceNumber(decodeURIComponent(referenceNumberProp));
    }
  }, [referenceNumberProp]);

  useEffect(() => {
    if (postalCodeProp !== undefined) {
      setPostalCode(postalCodeProp);
    }
  }, [postalCodeProp]);

  useEffect(() => {
    if (referenceNumberProp || postalCodeProp) {
      setActiveSearch('reference');
    } else if (trackingNumber) {
      setActiveSearch('tracking');
    }
  }, [trackingNumber, referenceNumberProp, postalCodeProp]);

  const handleTrack = (trackingNumber: string) => {
    setReferenceNumber('');
    setPostalCode('');
    handleAdobeAction('button', 'click', 'Track Shipment(Detail)');
    onTrack(trackingNumber);
  };

  const handleTrackReference = () => {
    setChrTrackingNumber('');
    handleAdobeAction('button', 'click', 'Track Reference(Detail)');
    onTrackReference?.(referenceNumber, postalCode);
  };

  const backgroundImage = isMobileScreen
    ? `url('./svg/mobile-header-background.svg')`
    : `url('./svg/header-background.svg')`;

  return (
    <Grid
      container
      data-testid="search-section"
      className="search-section"
      sx={{
        padding: isMobileScreen ? '30px 20px 45px 20px' : '30px 48px 45px 48px',
        backgroundImage,
        backgroundSize: 'cover',
      }}
    >
      <TitleSection
        isMobileScreen={isMobileScreen}
        translate={translate}
      />
      <Grid item xs={12} sx={{ marginTop: '15px' }}>
        {enhancedSearch ? (
          <EnhancedSearchSection
            activeSearch={activeSearch}
            setActiveSearch={setActiveSearch}
            chrTrackingNumber={chrTrackingNumber}
            setChrTrackingNumber={setChrTrackingNumber}
            referenceNumber={referenceNumber}
            setReferenceNumber={setReferenceNumber}
            postalCode={postalCode}
            setPostalCode={setPostalCode}
            handleTrack={handleTrack}
            handleTrackReference={handleTrackReference}
            isMobileScreen={isMobileScreen}
            translate={translate}
          />
        ) : (
          <LegacySearchSection
            chrTrackingNumber={chrTrackingNumber}
            setChrTrackingNumber={setChrTrackingNumber}
            handleTrack={handleTrack}
            isMobileScreen={isMobileScreen}
            translate={translate}
          />
        )}
      </Grid>
    </Grid>
  );
};
